import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { FormProvider, useForm } from 'react-hook-form'

import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

import {
  formOptions,
  useFieldValidation,
  flightConfirmationCodeConfig,
} from 'tripPlanner/components/TripModal/TripItems/Common/Fields/config'
import RHFInput from 'tripPlanner/components/TripModal/TripItems/Common/Fields/RHFInput'
import RHFDatePickerInput from 'tripPlanner/components/TripModal/TripItems/Common/Fields/RHFDatePickerInput'
import FlightLookupTile from 'tripPlanner/components/TripModal/TripItems/Search/FlightLookup/FlightLookupTile'
import { UppercaseInput, CustomLookupContainer, FindFlightButton } from 'tripPlanner/components/TripModal/TripItems/Search/FlightLookup/FlightLookupStyles'
import { useGetFlightSchedules } from 'tripPlanner/hooks/api/misc'
import { useAirports, useAirlineByCode } from 'tripPlanner/hooks/api/flights'
import { toggleHas } from 'lib/array/setUtils'
import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
  DMY_CASUAL_FORMAT,
} from 'constants/dateFormats'
import type { FlightSchedule } from 'tripPlanner/types/common'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'

type Step = 'lookup' | 'results'

interface LookupFormValues {
  flightCode: string
  departureDate: string
}

interface ResultsFormValues {
  confirmationCode?: string
}

interface Props {
  onSendMessage: (message: string) => void
}

const EMPTY_FLIGHT_ARRAY: Array<FlightSchedule> = []

function CustomFlightModal({ onSendMessage }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const { flightCarrierCodeConfig } = useFieldValidation()

  const [step, setStep] = useState<Step>('lookup')
  const [lookupData, setLookupData] = useState<{ flightCode: string; departureDate: string } | null>(null)
  const [selectedKeys, setSelectedKeys] = useState(new Set<string>())

  const handleClose = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  // --- Lookup form ---
  const lookupMethods = useForm<LookupFormValues>({
    ...formOptions,
    shouldUnregister: true,
    defaultValues: { flightCode: '', departureDate: '' },
  })

  const onLookupSubmit = useCallback((values: LookupFormValues) => {
    setLookupData({
      flightCode: values.flightCode.toUpperCase(),
      departureDate: values.departureDate,
    })
    setStep('results')
  }, [])

  // --- Results form ---
  const resultsMethods = useForm<ResultsFormValues>({
    ...formOptions,
    defaultValues: {},
  })

  const departureMoment = useMemo(
    () => lookupData ? moment(lookupData.departureDate) : moment(),
    [lookupData],
  )

  const {
    data: flights,
    isLoading: isFlightsLoading,
    isError: isFlightsError,
  } = useGetFlightSchedules(
    { departureDate: departureMoment, flightCode: lookupData?.flightCode ?? '' },
    {
      enabled: !!lookupData,
      onSuccess: (flights) => {
        setSelectedKeys(new Set(flights.map((f) => f.key)))
      },
    },
  )

  const toggleKey = useCallback(
    (id: string) =>
      setSelectedKeys((prev) => {
        const next = new Set([...prev])
        toggleHas(next, id)
        return next
      }),
    [],
  )

  const selectedFlights = useMemo(
    () => (flights ?? EMPTY_FLIGHT_ARRAY).filter((f) => selectedKeys.has(f.key)),
    [flights, selectedKeys],
  )
  const hasSelectedFlight = selectedFlights.length > 0

  const airportCodes = useMemo(() => {
    if (!flights) return []
    const codes = flights.flatMap((f) => [f.departureAirportCode, f.arrivalAirportCode])
    return [...new Set(codes)].sort()
  }, [flights])

  const airportResults = useAirports({ codes: airportCodes })

  const airportLoaded = useCallback(
    (code: string) => airportResults.some((r) => r.data?.airportCode === code),
    [airportResults],
  )

  const hasAllRequiredAirportData = useMemo(
    () => selectedFlights.every(
      (f) => airportLoaded(f.departureAirportCode) && airportLoaded(f.arrivalAirportCode),
    ),
    [selectedFlights, airportLoaded],
  )

  const airportsLoading = !hasAllRequiredAirportData && airportResults.some((r) => r.isLoading)

  const airlineCode = useMemo(() => {
    const first = selectedFlights[0]
    return first?.airlineCode
  }, [selectedFlights])

  const { data: airline } = useAirlineByCode({ code: airlineCode })

  const formatFlightMessage = useCallback(
    (values: ResultsFormValues) => {
      if (selectedFlights.length === 0 || !hasAllRequiredAirportData) return

      const displayParts: Array<string> = []
      const contextParts: Array<string> = []

      selectedFlights.forEach((flight) => {
        const departureAirport = airportResults.find(
          (r) => r.data?.airportCode === flight.departureAirportCode,
        )?.data
        const arrivalAirport = airportResults.find(
          (r) => r.data?.airportCode === flight.arrivalAirportCode,
        )?.data

        const flightCode = `${flight.airlineCode}${flight.flightNumber}`
        const from = departureAirport?.cityDesc ?? flight.departureAirportCode
        const to = arrivalAirport?.cityDesc ?? flight.arrivalAirportCode

        displayParts.push(
          intl.formatMessage(
            {
              id: 'FQuKvRFN6X5_WDr1v55r',
              defaultMessage: 'Add flight {flightCode} from {from} to {to}',
              description:
                'Visible user chat message shown when adding a custom flight with its route in trip planner chat',
            },
            {
              flightCode,
              from,
              to,
            },
          ),
        )

        const carrierLogo = airline?.airlineBrandMark ?? airline?.airlineLogo

        const details = [
          `Flight: ${flightCode}`,
          `Airline: ${flight.airlineName}`,
          `From: ${departureAirport?.airportName ?? flight.departureAirportCode} (${flight.departureAirportCode})`,
          `To: ${arrivalAirport?.airportName ?? flight.arrivalAirportCode} (${flight.arrivalAirportCode})`,
          `Departure: ${flight.departureDate.format(ISO_DATE_FORMAT)} at ${flight.departureTime.format(SHORT_TIME_FORMAT_24_HOUR)}`,
          `Arrival: ${flight.arrivalDate.format(ISO_DATE_FORMAT)} at ${flight.arrivalTime.format(SHORT_TIME_FORMAT_24_HOUR)}`,
        ]
        if (carrierLogo) {
          details.push(`Carrier logo: ${carrierLogo}`)
        }
        if (values.confirmationCode) {
          details.push(`Confirmation: ${values.confirmationCode}`)
        }
        contextParts.push(details.join('\n'))
      })

      const displayMessage = displayParts.join('\n')
      const contextMessage = `Please add the following custom flight to my trip:\n\n${contextParts.join('\n\n')}`
      const message = appendContextTag(displayMessage, contextMessage, TRIP_PLANNER_ACTION_CONTEXT_LABEL)
      onSendMessage(message)
      modalElement.resolve(undefined)
    },
    [intl, selectedFlights, hasAllRequiredAirportData, airportResults, airline, onSendMessage, modalElement],
  )

  const handleManualEntry = useCallback(() => {
    onSendMessage(
      intl.formatMessage({
        id: 'yPPtmxEHClPKcdT717mP',
        defaultMessage: 'I’d like to manually add a custom flight to my trip. Can you help me with that?',
        description:
          'Visible user chat message sent when the traveller chooses to manually add a custom flight in trip planner chat',
      }),
    )
    modalElement.resolve(undefined)
  }, [intl, onSendMessage, modalElement])

  const handleGoBack = useCallback(() => {
    setStep('lookup')
    setLookupData(null)
    setSelectedKeys(new Set())
  }, [])

  const today = useMemo(() => moment(), [])
  const minDate = useMemo(() => today.clone().subtract(1, 'days').toDate(), [today])
  const maxDate = useMemo(() => today.clone().add(12, 'months').toDate(), [today])

  const flightTitle = intl.formatMessage({
    id: 'iXuWvS8uvo92Ukh_mtl_',
    defaultMessage: 'Flight',
    description: 'Modal title for custom flight lookup in chat trip planner',
  })
  const findingFlightTitle = intl.formatMessage({
    id: 'Ls1TgvR-oxVYPFAp96Rp',
    defaultMessage: 'Finding flight...',
    description: 'Modal title while searching for flight in chat trip planner',
  })
  const flightNotFoundTitle = intl.formatMessage({
    id: '3sMQloTUE0GBuT86tTBe',
    defaultMessage: 'Flight not found',
    description: 'Modal title when flight not found in chat trip planner',
  })
  const flightDetailsTitle = intl.formatMessage({
    id: 'B_cYJYyiGoZx5-zPS_M1',
    defaultMessage: 'Flight details',
    description: 'Modal title when flight details found in chat trip planner',
  })

  if (step === 'results' && lookupData) {
    let title = flightTitle
    if (isFlightsLoading) title = findingFlightTitle
    else if (isFlightsError || (flights?.length === 0)) title = flightNotFoundTitle
    else if (flights && flights.length > 0) title = flightDetailsTitle

    const primaryDisabled = airportsLoading || !hasSelectedFlight
    const flightQuestion = flights && flights.length > 1
      ? intl.formatMessage({
          id: 'CVLj6DML_pzywoR-wmQJ',
          defaultMessage: 'Are these your flights?',
          description: 'Question shown when multiple flights found in chat custom flight lookup',
        })
      : intl.formatMessage({
          id: '3qvxegpN2IWpW8aFkLhg',
          defaultMessage: 'Is this your flight?',
          description: 'Question shown when single flight found in chat custom flight lookup',
        })

    return (
      <Modal
        title={title}
        onClose={handleClose}
        onBack={handleGoBack}
        tabletHeight="auto"
        size="S"
      >
        <VerticalSpacer gap={20}>
          <BodyText variant="large">
            <FormattedMessage
              id="nnuyXDoQAzGR1nu3bfZe"
              defaultMessage="<bold>{flightCode}</bold> departing on <bold>{departureDate}</bold>"
              description="Flight code and departure date display in chat custom flight lookup"
              values={{
                flightCode: lookupData.flightCode,
                departureDate: departureMoment.format(DMY_CASUAL_FORMAT),
                bold: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>

          {flights && flights.length > 0 && (
            <FormProvider {...resultsMethods}>
              <form onSubmit={resultsMethods.handleSubmit(formatFlightMessage)}>
                <VerticalSpacer gap={16}>
                  <BodyText variant="large">
                    {flightQuestion}
                  </BodyText>
                  {flights.map((flight) => (
                    <FlightLookupTile
                      key={flight.key}
                      flight={flight}
                      showSelect={flights.length > 1}
                      selected={selectedKeys.has(flight.key)}
                      onClick={toggleKey}
                    />
                  ))}
                  <Group
                    direction="horizontal"
                    horizontalAlign="stretch"
                    verticalAlign="center"
                    gap={16}
                  >
                    <RHFInput
                      {...resultsMethods.register('confirmationCode', {
                        ...flightConfirmationCodeConfig,
                      })}
                      label={intl.formatMessage({
                        id: 't8UMEsZMB9F8q8j-QpQb',
                        defaultMessage: 'Confirmation code',
                        description: 'Label for flight confirmation code in chat custom flight modal',
                      })}
                      placeholder={intl.formatMessage({
                        id: '_bD9q3A8KhuQ7_PTLPFy',
                        defaultMessage: 'Confirmation code',
                        description: 'Placeholder for flight confirmation code in chat custom flight modal',
                      })}
                    />
                  </Group>
                  <TextButton
                    kind="primary"
                    size="large"
                    fit="full-width"
                    type="submit"
                    disabled={primaryDisabled}
                  >
                    {intl.formatMessage({
                      id: 'CjT0b9xE8XsjLhS3bDbN',
                      defaultMessage: 'Add to trip',
                      description: 'Button to confirm and add custom flight to trip in chat custom flight modal',
                    })}
                  </TextButton>
                  <TextButton
                    kind="tertiary"
                    size="large"
                    fit="full-width"
                    onClick={handleManualEntry}
                  >
                    {intl.formatMessage({
                      id: 'IQfnuNnu7K4eYuRt9bgi',
                      defaultMessage: 'or enter details manually',
                      description: 'Button to manually enter flight details in chat custom flight modal',
                    })}
                  </TextButton>
                </VerticalSpacer>
              </form>
            </FormProvider>
          )}

          {isFlightsLoading && (
            <TextLoadingBox typography="body-large" lines={2} width="100%" />
          )}

          {isFlightsError && (
            <>
              <BodyText variant="large">
                {intl.formatMessage({
                  id: 'vAf7mOl1gd9_xB2V1fKo',
                  defaultMessage: 'Something went wrong looking up this flight. Please try again.',
                  description: 'Error message shown when the flight lookup API fails in the chat custom flight modal',
                })}
              </BodyText>
              <VerticalSpacer gap={16}>
                <TextButton
                  kind="primary"
                  size="large"
                  fit="full-width"
                  onClick={handleGoBack}
                >
                  {intl.formatMessage({
                    id: 'vzL7KNsni72z6jX43-PA',
                    defaultMessage: 'Try again',
                    description: 'Button to retry the flight search after an API error in chat custom flight modal',
                  })}
                </TextButton>
                <TextButton
                  kind="tertiary"
                  size="large"
                  fit="full-width"
                  onClick={handleManualEntry}
                >
                  {intl.formatMessage({
                    id: '11TU2o8u_EFi_Hb0yrPH',
                    defaultMessage: 'or enter details manually',
                    description: 'Button to manually enter flight details after an API error in chat custom flight modal',
                  })}
                </TextButton>
              </VerticalSpacer>
            </>
          )}

          {!isFlightsLoading && flights?.length === 0 && (
            <>
              <BodyText variant="large">
                {intl.formatMessage({
                  id: 'K4jImuYgQ8P97zsoy94M',
                  defaultMessage: "Unfortunately we couldn't find any flights matching your details.",
                  description: 'Message when no flights found in chat custom flight lookup',
                })}
              </BodyText>
              <VerticalSpacer gap={16}>
                <TextButton
                  kind="primary"
                  size="large"
                  fit="full-width"
                  onClick={handleGoBack}
                >
                  {intl.formatMessage({
                    id: 'KDJnjCXx-eMNUZnvBADC',
                    defaultMessage: 'Try another search',
                    description: 'Button to go back and try another flight search in chat',
                  })}
                </TextButton>
                <TextButton
                  kind="tertiary"
                  size="large"
                  fit="full-width"
                  onClick={handleManualEntry}
                >
                  {intl.formatMessage({
                    id: 'zLRMbpXOF3rHgbqb0gzE',
                    defaultMessage: 'or enter details manually',
                    description: 'Button to manually enter flight details when lookup fails in chat',
                  })}
                </TextButton>
              </VerticalSpacer>
            </>
          )}
        </VerticalSpacer>
      </Modal>
    )
  }

  return (
    <Modal
      title={flightTitle}
      onClose={handleClose}
      tabletHeight="auto"
      size="S"
    >
      <Group direction="vertical" gap={16}>
        <FormProvider {...lookupMethods}>
          <form onSubmit={lookupMethods.handleSubmit(onLookupSubmit)}>
            <CustomLookupContainer>
              <UppercaseInput
                {...lookupMethods.register('flightCode', {
                  ...flightCarrierCodeConfig,
                  setValueAs: (val: string) => val.toUpperCase(),
                })}
                required
                label={intl.formatMessage({
                  id: 'nkUG54odEBghUnmo_abA',
                  defaultMessage: 'Flight number',
                  description: 'Label for flight number input in chat custom flight modal',
                })}
                placeholder={intl.formatMessage({
                  id: 'DbamkgRXvdLgoryG-aIf',
                  defaultMessage: 'e.g. QF1',
                  description: 'Placeholder for flight number input in chat custom flight modal',
                })}
              />
              <RHFDatePickerInput
                name="departureDate"
                label={intl.formatMessage({
                  id: 'YFb--agF3PkararnFKRJ',
                  defaultMessage: 'Departure day',
                  description: 'Label for departure date picker in chat custom flight modal',
                })}
                min={minDate}
                max={maxDate}
                required
              />
              <VerticalSpacer gap={8}>
                <br />
                <FindFlightButton
                  kind="primary"
                  size="large"
                  type="submit"
                  fit="full-width"
                >
                  {intl.formatMessage({
                    id: '1ivtMqEFmnVZ3d5_nRD5',
                    defaultMessage: 'Find',
                    description: 'Button to look up flight in chat custom flight modal',
                  })}
                </FindFlightButton>
              </VerticalSpacer>
            </CustomLookupContainer>
          </form>
        </FormProvider>

        <Group direction="horizontal" gap={16} verticalAlign="center">
          <Divider kind="primary" />
          <BodyText variant="medium" weight="bold" colour="neutral-three">
            {intl.formatMessage({
              id: 'nj5sICHtSh5IR2tM70-i',
              defaultMessage: 'or',
              description: 'Divider text between flight lookup and manual entry in chat custom flight modal',
            })}
          </BodyText>
          <Divider kind="primary" />
        </Group>

        <FindFlightButton
          kind="secondary"
          size="large"
          onClick={handleManualEntry}
        >
          {intl.formatMessage({
            id: 'p1y_USjJEYEDkohPI6Hf',
            defaultMessage: 'Manually add details',
            description: 'Button to manually enter flight details in chat custom flight modal',
          })}
        </FindFlightButton>
      </Group>
    </Modal>
  )
}

export default CustomFlightModal
